// SUB-OFFICE 7 — THE PARKING LOT AT DUSK (zone: arrival_exterior).
// Build increment M-A / graphics Increment 7. The game opens OUTSIDE, and the
// outside is the argument for going in: an empty asphalt lot at permanent dusk,
// sodium cobra-heads buzzing warm sick pools, and a windowless mega-datacenter
// facade rising straight up until the fog eats it — the top is never rendered
// because the top is never seen. One human-scale door in a 40m wall; the scale
// joke IS the composition. The only warm pixel is the dispenser's red pilot
// glimpsed through the door.
//
// This is a boot-only scene: built on a fresh identity / post-72h / post-reissue
// and disposed the instant you cross the threshold. It owns its own local frame
// (approach axis runs south->north toward the door), its own collision grid, and
// its own chyron cornice. Everything batches into ~12 draw calls.

import {
  AdditiveBlending, BackSide, BoxGeometry, BufferAttribute, BufferGeometry,
  CanvasTexture, CircleGeometry, Color, CylinderGeometry, DoubleSide, Group, Matrix4,
  Mesh, MeshBasicMaterial, MeshLambertMaterial, PlaneGeometry, SphereGeometry,
  type Material,
} from 'three';
import { PAL, css } from '../../palette';
import { patchMaterial } from '../../render/scene';
import { Chyron, drawDotText, dotTextWidth, mkCanvas, toTexture } from '../../render/textures';
import type { CollisionBounds } from '../collision';
import type { ZoneManifest, ZoneSpawn, WallSeg, SolidRect, ZoneRect } from './types';
import machineFloor from '../../../content/machine-floor.json';

// --- exterior-only palette constants (spec: local constants, exterior only) ---
const SODIUM = 0xd98a21;       // VAULT AMBER pushed warm — the cobra-heads
const DUSK_ZENITH = 0x14161c;  // carbon at the top of the sky
const DUSK_MID = 0x2c3340;     // bruised blue-grey
const DUSK_HORIZON = 0x5a4a3a; // sodium-stained horizon
const ASPHALT = 0x201e1b;      // carbon lifted ~6%
const COOL_DOOR = 0xcfe0ee;    // the only cool light outdoors (interior spill)

// --- local frame -------------------------------------------------------------
// X west(-)/east(+), Z north(-, facade/door)/south(+, far-edge spawn), matching
// the interior convention. Camera looks north toward the door.
const APRON_X = 20;            // half-width: x[-20,20] (40m wide)
const FACADE_Z = -22;          // the wall / door plane (north)
const FAR_Z = 28;              // far edge (south) — the spawn
const DOOR_X = 0;
const DOOR_W = 2.1;            // one 2.1m human door in a 40m wall
const DOOR_H = 2.6;
const LAMP_Y = 6.2;            // cobra-head height
const FACADE_TOP = 62;         // facade height; fog eats it long before the top

const cx = (hex: number): Color => new Color(hex).convertSRGBToLinear();

// ============================================================================
// GeoBatch — merge transformed geometries into one non-indexed soup so a whole
// material's worth of props is a single draw call. (Same idea as world.ts's
// internal Batch, kept local so the exterior scene stays self-contained.)
// ============================================================================

interface UvRegion { u0: number; v0: number; u1: number; v1: number }
const FULL_UV: UvRegion = { u0: 0, v0: 0, u1: 1, v1: 1 };

class GeoBatch {
  private pos: number[] = [];
  private nor: number[] = [];
  private uv: number[] = [];

  add(geo: BufferGeometry, m: Matrix4, region: UvRegion = FULL_UV): void {
    const g = geo.index ? geo.toNonIndexed() : geo;
    g.applyMatrix4(m);
    const p = g.attributes.position.array;
    const n = g.attributes.normal ? g.attributes.normal.array : null;
    const u = g.attributes.uv ? g.attributes.uv.array : null;
    const count = g.attributes.position.count;
    for (let i = 0; i < count; i++) {
      this.pos.push(p[i * 3], p[i * 3 + 1], p[i * 3 + 2]);
      if (n) this.nor.push(n[i * 3], n[i * 3 + 1], n[i * 3 + 2]);
      else this.nor.push(0, 1, 0);
      const su = u ? u[i * 2] : 0;
      const sv = u ? u[i * 2 + 1] : 0;
      this.uv.push(region.u0 + su * (region.u1 - region.u0), region.v0 + sv * (region.v1 - region.v0));
    }
    if (g !== geo) g.dispose();
    geo.dispose();
  }

  get empty(): boolean { return this.pos.length === 0; }
  get tris(): number { return this.pos.length / 9; }

  build(): BufferGeometry {
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(new Float32Array(this.pos), 3));
    g.setAttribute('normal', new BufferAttribute(new Float32Array(this.nor), 3));
    g.setAttribute('uv', new BufferAttribute(new Float32Array(this.uv), 2));
    return g;
  }
}

const M = (x: number, y: number, z: number, sx = 1, sy = 1, sz = 1): Matrix4 =>
  new Matrix4().makeScale(sx, sy, sz).setPosition(x, y, z);

// ============================================================================
// Procedural textures (all canvas, zero assets — the house style)
// ============================================================================

/** Dusk gradient: carbon zenith -> bruised blue -> sodium-stained horizon. */
function makeSkyTexture(): CanvasTexture {
  const [c, ctx] = mkCanvas(8, 256);
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0.0, css(DUSK_ZENITH));
  g.addColorStop(0.55, css(DUSK_MID));
  g.addColorStop(0.86, css(DUSK_HORIZON));
  g.addColorStop(1.0, css(0x2a2018));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 8, 256);
  return toTexture(c);
}

/** Banded carbon-dark bone facade with a hairline seam grid at ~2m. Tiled. */
function makeFacadeTexture(): CanvasTexture {
  const [c, ctx] = mkCanvas(256, 256);
  ctx.fillStyle = css(0x24211c); // carbon-dark bone
  ctx.fillRect(0, 0, 256, 256);
  // faint horizontal banding (precast panel courses)
  for (let y = 0; y < 256; y += 8) {
    ctx.fillStyle = (y / 8) % 2 === 0 ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.03)';
    ctx.fillRect(0, y, 256, 8);
  }
  // hairline seam grid (~2m spacing -> the tile is 4m so seams at 0/128/256)
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.lineWidth = 1;
  for (const p of [0, 128]) {
    ctx.beginPath(); ctx.moveTo(p + 0.5, 0); ctx.lineTo(p + 0.5, 256); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, p + 0.5); ctx.lineTo(256, p + 0.5); ctx.stroke();
  }
  const t = toTexture(c, { repeat: true });
  t.repeat.set(APRON_X * 2 / 4, FACADE_TOP / 4); // 4m tile
  return t;
}

/**
 * The lot floor: carbon asphalt, grain, painted stall lines (bone at 30% wear)
 * in the thousands, oil-stain ellipses, the sodium pools painted under each
 * lamp, and one painted arrow at the door. UV [0,1] maps world x[-20,20] (u)
 * and z[FAR_Z .. FACADE_Z] with v=0 at the far edge (south) -> v=1 at the door.
 */
function makeAsphaltTexture(lampsXZ: [number, number][]): CanvasTexture {
  const S = 1024;
  const [c, ctx] = mkCanvas(S, S);
  const uOf = (x: number): number => ((x + APRON_X) / (APRON_X * 2)) * S;
  const vOf = (z: number): number => ((z - FAR_Z) / (FACADE_Z - FAR_Z)) * S; // far=0 door=S
  ctx.fillStyle = css(ASPHALT);
  ctx.fillRect(0, 0, S, S);
  // grain
  for (let i = 0; i < 9000; i++) {
    const a = Math.random();
    ctx.fillStyle = a > 0.5 ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.05)';
    ctx.fillRect(Math.random() * S, Math.random() * S, 1.5, 1.5);
  }
  // painted sodium pools under the lamps (elliptical, warm, worn)
  for (const [lx, lz] of lampsXZ) {
    const px = uOf(lx); const py = vOf(lz);
    const rx = 150; const ry = 120;
    const g = ctx.createRadialGradient(px, py, 6, px, py, rx);
    g.addColorStop(0, 'rgba(217,138,33,0.42)');
    g.addColorStop(0.5, 'rgba(217,138,33,0.16)');
    g.addColorStop(1, 'rgba(217,138,33,0)');
    ctx.save(); ctx.translate(px, py); ctx.scale(1, ry / rx); ctx.translate(-px, -py);
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(px, py, rx, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
  // stall grid: bone lines at 30% wear, numbers in the thousands near the door
  ctx.lineWidth = 3;
  const stallW = uOf(5.2) - uOf(0); // ~5.2m stalls
  const wear = (): string => `rgba(233,228,216,${0.18 + Math.random() * 0.12})`;
  let stallNo = 4108;
  for (let x = -APRON_X + 2; x <= APRON_X - 2; x += 5.2) {
    for (let z = FACADE_Z + 6; z <= FAR_Z - 4; z += 6) {
      const sx = uOf(x); const sz0 = vOf(z); const sz1 = vOf(z + 6);
      ctx.strokeStyle = wear();
      ctx.beginPath(); ctx.moveTo(sx, sz0); ctx.lineTo(sx, sz1); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(sx + stallW, sz0); ctx.lineTo(sx + stallW, sz1); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(sx, sz0); ctx.lineTo(sx + stallW, sz0); ctx.stroke();
      // number the row nearest the door, in the thousands; 4117 lit/occupied-feeling
      if (z <= FACADE_Z + 12) {
        const lit = stallNo === 4117;
        ctx.save();
        ctx.translate(sx + stallW / 2, (sz0 + sz1) / 2);
        ctx.rotate(Math.PI); // face a walker coming from the south
        ctx.fillStyle = lit ? 'rgba(217,138,33,0.55)' : 'rgba(233,228,216,0.22)';
        ctx.font = 'bold 34px monospace';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(String(stallNo).padStart(4, '0'), 0, 0);
        ctx.restore();
      }
      stallNo++;
    }
  }
  // oil-stain ellipses, seeded sparse
  for (let i = 0; i < 14; i++) {
    const px = 80 + Math.random() * (S - 160);
    const py = 80 + Math.random() * (S - 160);
    const rx = 20 + Math.random() * 30;
    ctx.save(); ctx.translate(px, py); ctx.scale(1, 0.6 + Math.random() * 0.5);
    const g = ctx.createRadialGradient(0, 0, 2, 0, 0, rx);
    g.addColorStop(0, 'rgba(0,0,0,0.4)'); g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, rx, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
  // one painted arrow pointing at the door (near the far edge, aimed north)
  ctx.strokeStyle = 'rgba(233,228,216,0.5)';
  ctx.fillStyle = 'rgba(233,228,216,0.5)';
  ctx.lineWidth = 8;
  const ax = uOf(DOOR_X); const ay0 = vOf(FAR_Z - 8); const ay1 = vOf(FAR_Z - 18);
  ctx.beginPath(); ctx.moveTo(ax, ay0); ctx.lineTo(ax, ay1); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(ax, ay1 - 4); ctx.lineTo(ax - 16, ay1 + 18); ctx.lineTo(ax + 16, ay1 + 18);
  ctx.closePath(); ctx.fill();
  return toTexture(c);
}

/** A signage atlas: N carbon plates with bone dot-matrix text, wrapped to fit. */
function makeSignAtlas(lines: string[]): { texture: CanvasTexture; region: (i: number) => UvRegion } {
  const cols = 2;
  const rows = Math.ceil(lines.length / cols);
  const CW = 512; const CH = 256;
  const [c, ctx] = mkCanvas(CW * cols, CH * rows);
  ctx.fillStyle = 'rgba(0,0,0,0)';
  const wrap = (text: string, maxW: number, dot: number): string[] => {
    const words = text.split(' ');
    const out: string[] = []; let cur = '';
    for (const w of words) {
      const trial = cur ? cur + ' ' + w : w;
      if (dotTextWidth(trial, { dot }) > maxW && cur) { out.push(cur); cur = w; }
      else cur = trial;
    }
    if (cur) out.push(cur);
    return out;
  };
  for (let i = 0; i < lines.length; i++) {
    const col = i % cols; const row = (i / cols) | 0;
    const ox = col * CW; const oy = row * CH;
    // carbon plate with a bone border
    ctx.fillStyle = css(0x1a1815); ctx.fillRect(ox + 10, oy + 10, CW - 20, CH - 20);
    ctx.strokeStyle = 'rgba(233,228,216,0.28)'; ctx.lineWidth = 3;
    ctx.strokeRect(ox + 16, oy + 16, CW - 32, CH - 32);
    const dot = 5;
    const wrapped = wrap(lines[i], CW - 80, dot);
    const lineH = 7 * dot + 10;
    let ty = oy + CH / 2 - (wrapped.length - 1) * lineH / 2 - 14;
    for (const wln of wrapped) {
      const w = dotTextWidth(wln, { dot });
      drawDotText(ctx, wln, ox + (CW - w) / 2, ty, css(PAL.bone), { dot });
      ty += lineH;
    }
  }
  const t = toTexture(c, { nearest: true });
  const region = (i: number): UvRegion => {
    const col = i % cols; const row = (i / cols) | 0;
    return {
      u0: col / cols, u1: (col + 1) / cols,
      // canvas y grows down; three uv v grows up -> flip rows
      v0: 1 - (row + 1) / rows, v1: 1 - row / rows,
    };
  };
  return { texture: t, region };
}

/** PROCEED threshold stencil + a DO NOT STAND mark, packed into one atlas. */
function makeDecalAtlas(): { texture: CanvasTexture; proceed: UvRegion; noStand: UvRegion } {
  const [c, ctx] = mkCanvas(512, 512);
  // top half: PROCEED (bone, with a chevron) — the threshold stencil
  ctx.save();
  const bone = css(PAL.bone);
  ctx.fillStyle = bone;
  // chevron
  ctx.beginPath();
  ctx.moveTo(256, 40); ctx.lineTo(316, 96); ctx.lineTo(300, 96);
  ctx.lineTo(256, 60); ctx.lineTo(212, 96); ctx.lineTo(196, 96);
  ctx.closePath(); ctx.fill();
  const w = dotTextWidth('PROCEED', { dot: 6 });
  drawDotText(ctx, 'PROCEED', (512 - w) / 2, 130, bone, { dot: 6 });
  ctx.restore();
  // bottom half: DO NOT STAND (stamp red) — the Bureau's reach precedes its walls
  const red = css(PAL.stampRed);
  const w2 = dotTextWidth('DO NOT', { dot: 6 });
  const w3 = dotTextWidth('STAND', { dot: 6 });
  drawDotText(ctx, 'DO NOT', (512 - w2) / 2, 300, red, { dot: 6 });
  drawDotText(ctx, 'STAND', (512 - w3) / 2, 360, red, { dot: 6 });
  const t = toTexture(c, { nearest: true });
  return {
    texture: t,
    proceed: { u0: 0, u1: 1, v0: 0.5, v1: 1 },   // top canvas half (v flipped)
    noStand: { u0: 0, u1: 1, v0: 0, v1: 0.5 },   // bottom canvas half
  };
}

// ============================================================================
// The build
// ============================================================================

export interface ExteriorBuild {
  group: Group;
  manifest: ZoneManifest;
  spawn: ZoneSpawn;
  door: { x: number; z: number; reach: number };
  /** narrative prose fired once as the player passes each z (descending). */
  approachTriggers: { z: number; line: string }[];
  chyron: Chyron;
  chyronLines: string[];
  /** within this z of the door the cornice wakes (chyron silent until then). */
  thresholdZ: number;
  /** drive the one flickering cobra-head (k in [0,1]); controller keys it. */
  setFlicker: (k: number) => void;
  tris: number;
  dispose(): void;
}

export function buildExterior(reducedMotion: boolean): ExteriorBuild {
  const content = machineFloor.arrivalExterior;
  const group = new Group();
  const disposables: { dispose(): void }[] = [];
  const track = <T extends Material | CanvasTexture>(x: T): T => { disposables.push(x); return x; };

  // lamp layout: 5 cobra-heads flanking the approach axis (one flickers)
  const lamps: [number, number][] = [
    [-14, 16], [14, 16], [-14, -2], [14, -2], [0, -14],
  ];

  // signage placements (coords needed early so their posts fold into the metal
  // batch): posts flanking the approach + a couple facade-mounted. [x,z,y,yaw]
  const signSpots: [number, number, number, number][] = [
    [-18, 20, 1.8, Math.PI], [18, 20, 1.8, Math.PI],
    [-18, 8, 1.8, Math.PI], [18, 8, 1.8, Math.PI],
    [-18, -6, 1.8, Math.PI], [18, -6, 1.8, Math.PI],
    [-6, FACADE_Z + 0.2, 3.6, Math.PI], [6, FACADE_Z + 0.2, 3.6, Math.PI],
    [-10, FACADE_Z + 0.2, 1.9, Math.PI], [10, FACADE_Z + 0.2, 1.9, Math.PI],
  ];

  // --- materials -------------------------------------------------------------
  const skyMat = track(new MeshBasicMaterial({ map: track(makeSkyTexture()), side: BackSide, fog: false, depthWrite: false }));
  const asphaltMat = track(new MeshLambertMaterial({ map: track(makeAsphaltTexture(lamps)), flatShading: true }));
  patchMaterial(asphaltMat);
  const facadeMat = track(new MeshLambertMaterial({ map: track(makeFacadeTexture()), flatShading: true }));
  patchMaterial(facadeMat);
  const metalMat = track(new MeshLambertMaterial({ color: cx(PAL.carbon), flatShading: true }));
  patchMaterial(metalMat);
  const doorLeafMat = track(new MeshLambertMaterial({ color: cx(PAL.bone), flatShading: true }));
  patchMaterial(doorLeafMat);
  // emissives — the practicals. fog:false so the warm door + sodium heads read
  // as BEACONS punching through the dusk haze ("the only warm light in the
  // world"), instead of drowning in FogExp2 0.03 from across the lot.
  const sodiumSolid = track(new MeshBasicMaterial({ color: cx(SODIUM), fog: false, side: DoubleSide }));
  const sodiumGlow = track(new MeshBasicMaterial({ color: cx(SODIUM), transparent: true, opacity: 0.14, blending: AdditiveBlending, depthWrite: false, fog: false, side: DoubleSide }));
  const coolGlow = track(new MeshBasicMaterial({ color: cx(COOL_DOOR), transparent: true, opacity: 0.5, blending: AdditiveBlending, depthWrite: false, fog: false, side: DoubleSide }));
  const coolSolid = track(new MeshBasicMaterial({ color: cx(COOL_DOOR), fog: false, side: DoubleSide }));
  const pilotMat = track(new MeshBasicMaterial({ color: cx(PAL.stampRed), fog: false, side: DoubleSide }));

  // --- sky dome (radius sits inside the arrival far-plane of 400) -------------
  const dome = new Mesh(new SphereGeometry(200, 20, 12), skyMat);
  dome.position.set(0, 0, 0);
  dome.matrixAutoUpdate = false; dome.updateMatrix();
  group.add(dome);

  // --- monolith facade: three nested setback slabs, merged (1 draw call) -----
  const monolith = new GeoBatch();
  const setbacks: [number, number][] = [
    // [half-width, depth] rising and NARROWING as it climbs
    [APRON_X, 6], [APRON_X - 4, 5], [APRON_X - 9, 4],
  ];
  let yBase = 0;
  const seg = FACADE_TOP / 3;
  for (const [hw, depth] of setbacks) {
    monolith.add(
      new BoxGeometry(hw * 2, seg, depth),
      M(0, yBase + seg / 2, FACADE_Z - depth / 2),
    );
    yBase += seg;
  }
  const monolithMesh = new Mesh(monolith.build(), facadeMat);
  monolithMesh.matrixAutoUpdate = false;
  group.add(monolithMesh);

  // --- the lot floor ---------------------------------------------------------
  const ground = new Mesh(new PlaneGeometry(APRON_X * 2, FAR_Z - FACADE_Z), asphaltMat);
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(0, 0, (FACADE_Z + FAR_Z) / 2);
  ground.matrixAutoUpdate = false; ground.updateMatrix();
  group.add(ground);

  // --- the door assembly (leaf) + frame folded into metal --------------------
  const metal = new GeoBatch();
  // door frame (three carbon bars around the opening, proud of the facade)
  const fz = FACADE_Z + 0.06;
  metal.add(new BoxGeometry(DOOR_W + 0.5, 0.25, 0.2), M(DOOR_X, DOOR_H + 0.12, fz)); // lintel
  metal.add(new BoxGeometry(0.25, DOOR_H + 0.25, 0.2), M(DOOR_X - DOOR_W / 2 - 0.12, DOOR_H / 2, fz));
  metal.add(new BoxGeometry(0.25, DOOR_H + 0.25, 0.2), M(DOOR_X + DOOR_W / 2 + 0.12, DOOR_H / 2, fz));
  // the cool overhead fixture housing (the only cool light outdoors)
  metal.add(new BoxGeometry(1.1, 0.18, 0.4), M(DOOR_X, DOOR_H + 0.55, FACADE_Z + 0.3));
  // cobra-head lamps: 10-seg pole, single arm, head box — merged into metal
  for (const [lx, lz] of lamps) {
    const pole = new CylinderGeometry(0.11, 0.13, LAMP_Y, 8);
    metal.add(pole, M(lx, LAMP_Y / 2, lz));
    // arm reaches toward the approach axis (toward x=0)
    const dir = lx >= 0 ? -1 : 1;
    metal.add(new BoxGeometry(1.6, 0.12, 0.12), M(lx + dir * 0.8, LAMP_Y - 0.1, lz));
    metal.add(new BoxGeometry(0.7, 0.22, 0.34), M(lx + dir * 1.5, LAMP_Y - 0.25, lz)); // head box
  }
  // curb stops near the door row (chamfered-ish low boxes)
  for (let i = -2; i <= 2; i++) {
    metal.add(new BoxGeometry(1.6, 0.18, 0.3), M(i * 5.2, 0.09, FACADE_Z + 5));
  }
  // carbon posts beneath the free-standing signs (facade-mounted ones need none)
  for (const [x, z, y] of signSpots) {
    if (z > FACADE_Z + 1) metal.add(new CylinderGeometry(0.06, 0.06, y, 6), M(x, y / 2, z));
  }
  const metalMesh = new Mesh(metal.build(), metalMat);
  metalMesh.matrixAutoUpdate = false;
  group.add(metalMesh);

  // the leaf hangs AJAR — it covers the left ~57% of the opening, leaving a gap
  // on the +x side through which the interior (cool spill + red pilot) shows.
  const doorLeaf = new Mesh(new BoxGeometry(1.2, DOOR_H, 0.1), doorLeafMat);
  doorLeaf.position.set(DOOR_X - 0.45, DOOR_H / 2, FACADE_Z + 0.02);
  doorLeaf.matrixAutoUpdate = false; doorLeaf.updateMatrix();
  group.add(doorLeaf);

  // --- emissive: the cool interior spill through the AJAR gap -----------------
  // (x[0.15,1.05], just inside the plane — the building's cool light, the one
  // cool thing outdoors, against the sodium.)
  const coolInterior = new GeoBatch();
  const spill = new PlaneGeometry(0.9, DOOR_H - 0.15);
  coolInterior.add(spill, M(DOOR_X + 0.6, DOOR_H / 2, FACADE_Z - 0.05));
  const coolInteriorMesh = new Mesh(coolInterior.build(), coolSolid);
  coolInteriorMesh.matrixAutoUpdate = false;
  group.add(coolInteriorMesh);

  // one cobra-head FLICKERS on a hand-keyed loop (the exterior cousin of
  // Window 3). Its emissive underside + cone get their own materials so the
  // controller can gutter just that lamp; the other four burn steady.
  const FLICKER_LAMP = 4; // the lone lamp on the approach axis, [0,-14]
  const flickerSolidMat = track(new MeshBasicMaterial({ color: cx(SODIUM), fog: false, side: DoubleSide }));
  const flickerGlowMat = track(new MeshBasicMaterial({ color: cx(SODIUM), transparent: true, opacity: 0.14, blending: AdditiveBlending, depthWrite: false, fog: false, side: DoubleSide }));

  // --- sodium head undersides (the buzzing amber panels under each cobra-head) -
  const amberSolid = new GeoBatch();
  const flickerSolid = new GeoBatch();
  for (let i = 0; i < lamps.length; i++) {
    const [lx, lz] = lamps[i];
    const dir = lx >= 0 ? -1 : 1;
    const u = new PlaneGeometry(0.5, 0.24);
    const mat = new Matrix4().makeRotationX(Math.PI / 2).premultiply(M(lx + dir * 1.5, LAMP_Y - 0.36, lz));
    (i === FLICKER_LAMP ? flickerSolid : amberSolid).add(u, mat);
  }
  const amberSolidMesh = new Mesh(amberSolid.build(), sodiumSolid);
  amberSolidMesh.matrixAutoUpdate = false;
  group.add(amberSolidMesh);
  const flickerSolidMesh = new Mesh(flickerSolid.build(), flickerSolidMat);
  flickerSolidMesh.matrixAutoUpdate = false;
  group.add(flickerSolidMesh);

  // --- additive cone cards under each cobra-head -----------------------------
  const cones = new GeoBatch();
  const flickerCones = new GeoBatch();
  const coneGeo = (): BufferGeometry => {
    const g = new BufferGeometry();
    const top = 0.4; const bot = 3.2; const h = LAMP_Y - 0.4;
    const verts = new Float32Array([
      -top, h, 0, top, h, 0, bot, 0, 0,
      -top, h, 0, bot, 0, 0, -bot, 0, 0,
    ]);
    g.setAttribute('position', new BufferAttribute(verts, 3));
    g.setAttribute('normal', new BufferAttribute(new Float32Array(18).fill(0).map((_, i) => (i % 3 === 2 ? 1 : 0)), 3));
    g.setAttribute('uv', new BufferAttribute(new Float32Array(12), 2));
    return g;
  };
  for (let i = 0; i < lamps.length; i++) {
    const [lx, lz] = lamps[i];
    const dir = lx >= 0 ? -1 : 1;
    const hx = lx + dir * 1.5;
    // two crossed downward-tapering trapezoids (narrow at the head, wide on ground)
    for (const rot of [0, Math.PI / 2]) {
      const target = i === FLICKER_LAMP ? flickerCones : cones;
      target.add(coneGeo(), new Matrix4().makeRotationY(rot).premultiply(M(hx, 0, lz)));
    }
  }
  const coneMesh = new Mesh(cones.build(), sodiumGlow);
  coneMesh.matrixAutoUpdate = false;
  group.add(coneMesh);
  const flickerConeMesh = new Mesh(flickerCones.build(), flickerGlowMat);
  flickerConeMesh.matrixAutoUpdate = false;
  group.add(flickerConeMesh);
  // drive the flicker lamp's brightness (k in [0,1]); the controller keys it
  const setFlicker = (k: number): void => {
    flickerSolidMat.color.setHex(SODIUM).convertSRGBToLinear().multiplyScalar(0.15 + 0.85 * k);
    flickerGlowMat.opacity = 0.14 * (0.1 + 0.9 * k);
  };

  // the cool door pool on the ground (the building's light reaching out) + a
  // downward cone from the door fixture
  const coolBatch = new GeoBatch();
  const pool = new CircleGeometry(2.2, 20);
  coolBatch.add(pool, new Matrix4().makeRotationX(-Math.PI / 2).premultiply(M(DOOR_X, 0.03, FACADE_Z + 2.0)));
  const coolMesh = new Mesh(coolBatch.build(), coolGlow);
  coolMesh.matrixAutoUpdate = false;
  group.add(coolMesh);

  // the red pilot — the warmest pixel on screen, seen through the ajar gap,
  // sitting just IN FRONT of the cool spill so it reads as the one hot point
  const pilot = new Mesh(new PlaneGeometry(0.12, 0.12), pilotMat);
  pilot.position.set(DOOR_X + 0.6, 1.15, FACADE_Z - 0.03);
  pilot.matrixAutoUpdate = false; pilot.updateMatrix();
  group.add(pilot);

  // --- signage (10 verbatim lines) -> one merged mesh ------------------------
  const signAtlas = makeSignAtlas(content.signage);
  const signMat = track(new MeshLambertMaterial({ map: track(signAtlas.texture), transparent: true, side: DoubleSide }));
  patchMaterial(signMat);
  const signs = new GeoBatch();
  for (let i = 0; i < content.signage.length && i < signSpots.length; i++) {
    const [x, z, y, yaw] = signSpots[i];
    const board = new PlaneGeometry(2.0, 1.0);
    signs.add(board, new Matrix4().makeRotationY(yaw).premultiply(M(x, y, z)), signAtlas.region(i));
  }
  const signMesh = new Mesh(signs.build(), signMat);
  signMesh.matrixAutoUpdate = false;
  group.add(signMesh);

  // --- floor decals: PROCEED on the threshold + DO NOT STAND marks -----------
  const decal = makeDecalAtlas();
  const decalMat = track(new MeshBasicMaterial({ map: track(decal.texture), transparent: true, depthWrite: false }));
  patchMaterial(decalMat);
  const decals = new GeoBatch();
  const noStandZones: ZoneRect[] = [];
  // PROCEED on the door threshold
  const proceed = new PlaneGeometry(2.4, 1.2);
  decals.add(proceed, new Matrix4().makeRotationX(-Math.PI / 2).premultiply(M(DOOR_X, 0.02, FACADE_Z + 2.6)), decal.proceed);
  // DO NOT STAND marks continuing outside (the Bureau's reach precedes its walls)
  const noStandSpots: [number, number][] = [[-8, 10], [8, 2], [-4, -8], [10, 18]];
  for (const [x, z] of noStandSpots) {
    const q = new PlaneGeometry(1.4, 1.4);
    decals.add(q, new Matrix4().makeRotationX(-Math.PI / 2).premultiply(M(x, 0.02, z)), decal.noStand);
    noStandZones.push({ kind: 'noStand', x0: x - 0.7, z0: z - 0.7, x1: x + 0.7, z1: z + 0.7 });
  }
  const decalMesh = new Mesh(decals.build(), decalMat);
  decalMesh.matrixAutoUpdate = false;
  group.add(decalMesh);

  // --- the exterior chyron cornice (over the door; silent until threshold) ----
  const chyron = new Chyron(reducedMotion);
  const chyronMat = track(new MeshBasicMaterial({ map: chyron.texture, transparent: true }));
  patchMaterial(chyronMat, { dimExempt: true }); // the Machine's voice stays lit
  const cornice = new Mesh(new PlaneGeometry(8, 1.0), chyronMat);
  cornice.position.set(DOOR_X, DOOR_H + 1.4, FACADE_Z + 0.12);
  cornice.matrixAutoUpdate = false; cornice.updateMatrix();
  group.add(cornice);

  // --- collision: a closed apron with a solid facade (door crossed via E) -----
  const walls: WallSeg[] = [
    { x0: -APRON_X, z0: FACADE_Z, x1: APRON_X, z1: FACADE_Z, h: FACADE_TOP }, // facade (solid, incl. door)
    { x0: -APRON_X, z0: FAR_Z, x1: APRON_X, z1: FAR_Z, h: 2 },                 // far edge
    { x0: -APRON_X, z0: FACADE_Z, x1: -APRON_X, z1: FAR_Z, h: 2 },             // west
    { x0: APRON_X, z0: FACADE_Z, x1: APRON_X, z1: FAR_Z, h: 2 },              // east
  ];
  const solids: SolidRect[] = [];
  for (const [lx, lz] of lamps) solids.push({ x0: lx - 0.2, z0: lz - 0.2, x1: lx + 0.2, z1: lz + 0.2 });
  const bounds: CollisionBounds = { x0: -APRON_X - 1, z0: FACADE_Z - 1, x1: APRON_X + 1, z1: FAR_Z + 1 };

  const spawn: ZoneSpawn = { x: DOOR_X, z: FAR_Z - 2, facing: Math.PI }; // far edge, facing north (door)

  const manifest: ZoneManifest = {
    id: 'arrival_exterior',
    name: 'THE PARKING LOT AT DUSK',
    residency: 'boot-only',
    slabY: 0,
    bounds,
    spawns: { far_edge: spawn },
    connections: [{ toZone: 'floor7', kind: 'boot', triggerId: 'exterior_door', masked: true, overrideable: false }],
    collision: { walls, solids, zones: noStandZones },
    coexistWithResident: false,
  };

  // approach prose fires once per z-gate as you descend toward the door
  const fa = content.firstApproach;
  const approachTriggers = [
    { z: FAR_Z - 6, line: fa[0] },
    { z: 12, line: fa[1] },
    { z: -2, line: fa[2] },
    { z: FACADE_Z + 7, line: fa[3] },
  ];

  let tris = monolith.tris; // (batches disposed on build; approximate via counters)
  tris += 400; // dome + ground + door + emissives + decals, a coarse allowance

  const dispose = (): void => {
    group.traverse((o) => {
      const m = o as Mesh;
      if (m.geometry) m.geometry.dispose();
    });
    for (const d of disposables) d.dispose();
    chyron.texture.dispose();
    group.clear();
  };

  return {
    group, manifest, spawn,
    door: { x: DOOR_X, z: FACADE_Z, reach: 2.4 },
    approachTriggers, chyron, chyronLines: content.chyronLines,
    thresholdZ: FACADE_Z + 9, setFlicker, tris, dispose,
  };
}
